import { useEffect, useState, useMemo, type SyntheticEvent } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
    getTemplateInfo,
    setDocumentTemplateId,
    selectSectionsSortedByKey,
    selectTemplatesSortedByName,
} from "../../store/slices/configSlice";
import TemplateEditor from "./TemplateEditor";
import SectionEditor from "./SectionEditor";
import { format } from "date-fns";
import TabPanel from "../TabPanel";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/authContext";

import {
    Box,
    Table,
    TableBody,
    TableRow,
    TableCell,
    IconButton,
    Tab,
    Tabs,
    TextField,
} from "@mui/material";

import {
    Edit as EditIcon,
    Add as AddIcon,
    NoteAdd as NoteAddIcon,
} from "@mui/icons-material";

// configSlice's Template / Section shapes carry an index sig, so accessing the
// legacy `lastModifiedOn` / `question` fields stays typed as `any` without us
// having to widen the shared interface here.
type EditableTemplate = Record<string, any>;
type EditableSection = Record<string, any>;

const Templates = () => {
    const dispatch = useAppDispatch();
    const templates = useAppSelector(selectTemplatesSortedByName);
    const sections = useAppSelector(selectSectionsSortedByKey);

    const { user, hasRole } = useAuth();
    const [editingTemplate, setEditingTemplate] = useState<EditableTemplate | null>(null);
    const [editingSection, setEditingSection] = useState<EditableSection | null>(null);
    const [currentTab, setCurrentTab] = useState(0);
    const [sectionFilter, setSectionFilter] = useState("");

    const navigate = useNavigate();

    const isAdmin = useMemo(() => hasRole("TemplatesAdmin"), [hasRole]);

    useEffect(() => {
        if (!user) return;
        dispatch(getTemplateInfo());
    }, [user, dispatch]);

    const filteredSections = useMemo(() => {
        const filter = sectionFilter.trim().toLowerCase();
        if (filter === "") return sections;
        return sections.filter((s) => (s.key ?? "").toLowerCase().includes(filter));
    }, [sections, sectionFilter]);

    const handleTabChange = (_e: SyntheticEvent, newValue: number) => {
        setEditingTemplate(null);
        setEditingSection(null);
        setCurrentTab(newValue);
    };

    const addTemplate = () => setEditingTemplate({});
    const addSection = () => setEditingSection({});

    const createDocument = (id: number | string) => {
        dispatch(setDocumentTemplateId(id));
        navigate("/template-document");
    };

    if (!user) return null;

    return (
        <Box display="flex" flexDirection="column" style={{ paddingTop: "10px" }}>
            <Tabs value={currentTab} onChange={handleTabChange} style={{ marginBottom: "10px" }}>
                <Tab label="Templates" />
                {isAdmin && <Tab label="Sections" />}
            </Tabs>
            <TabPanel value={currentTab} index={0}>
                {!editingTemplate && (
                    <>
                        {isAdmin && (
                            <IconButton onClick={addTemplate} size="large">
                                <AddIcon />
                            </IconButton>
                        )}
                        <Table size="small">
                            <TableBody>
                                {templates.map((t) => (
                                    <TableRow key={t.templateId}>
                                        <TableCell>{t.name}</TableCell>
                                        <TableCell>{format(new Date(t.lastModifiedOn), "dd/MM/yyyy HH:mm")}</TableCell>
                                        {isAdmin && (
                                            <TableCell>
                                                <IconButton onClick={() => setEditingTemplate(t)} size="large">
                                                    <EditIcon />
                                                </IconButton>
                                            </TableCell>
                                        )}
                                        <TableCell>
                                            <IconButton onClick={() => createDocument(t.templateId)} size="large">
                                                <NoteAddIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </>
                )}
                {editingTemplate && (
                    <TemplateEditor template={editingTemplate} onSave={() => setEditingTemplate(null)} />
                )}
            </TabPanel>
            <TabPanel value={currentTab} index={1}>
                {!editingSection && (
                    <>
                        <Box display="flex" alignItems="center">
                            <IconButton onClick={addSection} size="large">
                                <AddIcon />
                            </IconButton>
                            <TextField
                                value={sectionFilter}
                                onChange={(e) => setSectionFilter(e.target.value)}
                                variant="outlined"
                                size="small"
                                label="filter"
                            />
                        </Box>
                        <Table size="small">
                            <TableBody>
                                {filteredSections.map((s) => (
                                    <TableRow key={s.sectionId}>
                                        <TableCell>{s.key}</TableCell>
                                        <TableCell>{s.question}</TableCell>
                                        <TableCell>{format(new Date(s.lastModifiedOn), "dd/MM/yyyy HH:mm")}</TableCell>
                                        <TableCell>
                                            <IconButton onClick={() => setEditingSection(s)} size="large">
                                                <EditIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </>
                )}
                {editingSection && (
                    <SectionEditor section={editingSection} onSave={() => setEditingSection(null)} />
                )}
            </TabPanel>
        </Box>
    );
};

export default Templates;
